import {
  createMulticastServiceType,
  ServiceContainer,
} from '@tv2-player/service-container/src/serviceContainer';

export type FilterType<FilterThing, Rest extends any[] = []> = {
  apply: (arg: FilterThing, ...rest: Rest) => Promise<FilterThing>;
  register: (
    callback: (arg: FilterThing, ...rest: Rest) => Promise<FilterThing>,
    priority?: number
  ) => void;
};
export type ActionType<Rest extends any[] = []> = {
  perform: (...args: Rest) => Promise<void>;
  register: (callback: (...args: Rest) => void, priority?: number) => void;
};

export function createFilter<FilterThing, Rest extends any[] = []>(): FilterType<
  FilterThing,
  Rest
> {
  const service = createMulticastServiceType<{
    callback: (arg: FilterThing, ...rest: Rest) => Promise<FilterThing>;
    priority: number;
  }>();
  return {
    apply: async (arg: FilterThing, ...rest: Rest) => {
      const transformers = ServiceContainer.get(service);
      transformers.sort((a, b) => a.priority - b.priority);

      let head = arg;
      // eslint-disable-next-line no-restricted-syntax
      for (const transformer of transformers) {
        // eslint-disable-next-line no-await-in-loop
        head = await transformer.callback(head, ...rest);
      }
      return head;
    },
    register: (
      callback: (arg: FilterThing, ...rest: Rest) => Promise<FilterThing>,
      priority = 10
    ) => {
      ServiceContainer.register(service).instance({ callback, priority });
    },
  };
}

export function createAction<Rest extends any[] = []>(): ActionType<Rest> {
  const service = createMulticastServiceType<{
    callback: (...args: Rest) => void | Promise<void>;
    priority: number;
  }>();
  return {
    perform: async (...args: Rest) => {
      const actions = ServiceContainer.get(service);
      actions.sort((a, b) => a.priority - b.priority);

      await Promise.all(actions.map(action => action.callback(...args)));
    },
    register: (callback: (...args: Rest) => void, priority = 10) => {
      ServiceContainer.register(service).instance({ callback, priority });
    },
  };
}
